import {
  Dialog,
  DialogContent,
  Grid,
  Message,
  styled,
} from '@deliveryhero/armor'
import i18n from 'i18next'
import 'react-toastify/dist/ReactToastify.min.css'
import { CircularProgress } from '@material-ui/core'
import { useLocation, useNavigate } from '@reach/router'
import axios from 'axios'
import React, { useEffect, useState } from 'react'
import {
  I18nextProvider,
  useTranslation,
  initReactI18next,
} from 'react-i18next'
import { LandingPageRenderer } from '../../components/LandingPageRenderer'
import { Layout } from '../../components/layout'
import { DenormalizedLandingPage } from '../../containers/landing-page/selectors'
import { report } from '../../utils/gtm'
import { toastError } from '../../components/toasts/Error'
import {
  FORM_1_SUBMIT,
  FORM_PAGE,
  PAGE_VIEW,
  FORM_2_SUBMIT,
} from '../../utils/gtm/constants'
import { requestCreator } from './request-creator'

i18n.use(initReactI18next).init({
  resources: {},
  lng: 'en',
  fallbackLng: 'en',
  debug: true,
  interpolation: {
    escapeValue: false,
    formatSeparator: ',',
  },
})

const CenteredGrid = styled(Grid)`
  display: flex;
  justify-content: center;
  align-items: center;
`
const LandingPage: React.FC<{
  pageContext: {
    landingPage: DenormalizedLandingPage
    landingPageContentIndex: number
    resources: Record<string, string>
  }
}> = ({ pageContext: { landingPage, landingPageContentIndex, resources } }) => {
  const lng =
    landingPage.landingPageContents[landingPageContentIndex].language.id

  if (!i18n.hasResourceBundle(lng, 'translation')) {
    i18n.addResourceBundle(
      lng,
      'translation',
      resources[lng] ? resources[lng].translation : resources.en.translation,
    )
    i18n.changeLanguage(lng)
  }

  const [t] = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(null)

  useEffect(() => {
    report({
      event: PAGE_VIEW,
      pageUrlPath: location.pathname,
    })
  }, [location.pathname])

  const errorPretiffier = {
    duplicate: t('LandingPage.DuplicateApplicant'),
  }

  // Required to track users across domains
  const getLinkerParam = () => {
    const myWindow: typeof window & {
      ga?: { getAll: () => { get: (param: string) => string }[] }
    } = window

    if (
      typeof myWindow.ga !== 'undefined' &&
      myWindow.ga.getAll &&
      myWindow.ga.getAll().length
    ) {
      const linkerParamParts = myWindow.ga
        .getAll()[0]
        .get('linkerParam')
        .split('=')

      if (linkerParamParts.length >= 2) {
        return linkerParamParts
      }
    }

    return []
  }

  const isUnderAge = (age: string) => age !== 'yes'

  const onFormSubmit = (data: Record<string, string>) => {
    // TO-DO Add more validations
    if (data?.age && isUnderAge(data.age)) {
      toastError(t('LandingPage.YouCannotApplyIfUnderage'))
      window.scrollTo(0, 0)
    } else {
      setIsLoading(true)

      const url = new URL(`${process.env.GATSBY_APPLICANT_CREATION_ENDPOINT}`)
      const linkerParam = getLinkerParam()

      if (linkerParam.length)
        url.searchParams.append(linkerParam[0], linkerParam[1])

      report({
        event: FORM_2_SUBMIT,
        formElements: Object.keys(data).length,
      })

      axios
        .post(
          url.toString(),
          requestCreator(
            landingPage.landingPageContents[landingPageContentIndex],
            data,
          ),
        )
        .then(response => {
          setHasError(null)
          navigate(
            `${process.env.GATSBY_ARARA_DASHBOARD_URL}/public/application/${response.data.data.id}`,
          )
        })
        .catch(err => {
          console.error(err)
          window.scrollTo(0, 0)

          if (err.response?.data?.errors?.length) {
            if (err.response.data.errors[0].detail === 'duplicate') {
              navigate(
                `${process.env.GATSBY_ARARA_DASHBOARD_URL}/public/application/duplicate`,
              )
            } else {
              setHasError(errorPrettifier[err.response.data.errors[0].detail])
            }
          } else {
            setHasError(
              `${t('LandingPage.StatusCode')}: ${err.response.status}`,
            )
          }
        })
        .then(() => setIsLoading(false))
    }
  }

  const onFieldChange = (data: Record<string, string>, name: string) => {
    if (name === 'vehicle')
      report({
        event: FORM_1_SUBMIT,
        pageType: FORM_PAGE,
        Brand: landingPage.theme.brandCode.toUpperCase(),
        cityEntered: data.city,
        vehicleEntered: data.vehicle,
      })
  }

  const switchLanguage = (newLandingPageContentIndex: number) => {
    const newPath = `${
      window.location.origin
    }${window.location.pathname.replace(
      `/${landingPage.landingPageContents[landingPageContentIndex].language.id}`,
      '',
    )}${
      newLandingPageContentIndex !== 0
        ? landingPage.landingPageContents[newLandingPageContentIndex].language
            .id
        : ''
    }/`

    console.log('Redirecting to: ', newPath)

    window.location.href = newPath
  }

  return (
    <I18nextProvider i18n={i18n}>
      <Layout
        mainClass="landing-page-root"
        headless
        seo={landingPage.landingPageContents[landingPageContentIndex].seo}
        iconUrl={landingPage.theme.logoIcon}
      >
        {isLoading && (
          <Dialog open={isLoading} disableCloseButton minWidth="150px">
            <DialogContent>
              <CenteredGrid>
                <CircularProgress />
              </CenteredGrid>
            </DialogContent>
          </Dialog>
        )}

        {hasError !== null && (
          <Message error id="error-container" onClose={() => setHasError(null)}>
            {t('LandingPage.ThereWasAnError')}: {hasError}
          </Message>
        )}

        <LandingPageRenderer
          previewMode={false}
          landingPage={landingPage}
          selectedLandingPageContentIdx={landingPageContentIndex}
          onFormSubmit={onFormSubmit}
          onFieldChange={onFieldChange}
          onChangeLandingPageContextIdx={switchLanguage}
        />
      </Layout>
    </I18nextProvider>
  )
}

export default LandingPage
